import { useEffect, useRef, useState } from 'react';

type Submission = {
    paymentRequestId?: string;
    inFlight: boolean;
    completed: boolean;
};

export const useTransactionSubmission = (scopeKey: string) => {
    const submission = useRef<Submission>({ inFlight: false, completed: false });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        submission.current = { inFlight: false, completed: false };
        setIsSubmitting(false);
    }, [scopeKey]);

    const submit = async (operation: (paymentRequestId: string) => Promise<unknown>) => {
        const { current } = submission;
        // React's loading state alone cannot stop two submits in the same render.
        if (current.inFlight || current.completed) {
            return;
        }

        current.paymentRequestId ??= crypto.randomUUID();
        current.inFlight = true;
        setIsSubmitting(true);
        try {
            await operation(current.paymentRequestId);
            current.completed = true;
        } finally {
            current.inFlight = false;
            if (submission.current === current) {
                setIsSubmitting(false);
            }
        }
        // Keep the same ID after an error: the server may have saved the payment even if
        // the response never arrived. A new modal operation resets the ID via scopeKey.
    };

    return { isSubmitting, submit };
};
